import { HttpResponse } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { NavController } from "@ionic/angular";
import { Subject, Subscription } from "rxjs";
import { extractDuplicatedDemandId } from "src/app/helpers/demande-response";
import { LocalStorageService } from "./localStorage.service";
import { PusherService } from "./pusher.service";

/** Implemented by services-around when mounted to reload map + demand state. */
export interface ParticulierRelanceReloadHandler {
  reloadForNewDemand(newDemandId: number, sourceDemandId: number): Promise<void>;
}

/**
 * Coordinates duplicate/relance across particulier flows.
 * The relance modal UI lives only in particulier-footer; other pages request it here.
 */
@Injectable({ providedIn: "root" })
export class ParticulierRelanceFlowService implements OnDestroy {
  /** Target demand id during an in-flight relance navigation/reload. */
  relanceNavigationDemandId: number | null = null;

  private openModalRequest$ = new Subject<number>();
  /** Footer subscribes and presents the single relance modal. */
  readonly relanceModalRequest$ = this.openModalRequest$.asObservable();

  private reloadHandler: ParticulierRelanceReloadHandler | null = null;
  private relancing = false;
  private suppressCancelUntil = 0;
  private lastRelanceModalDemandId: number | null = null;
  private lastRelanceModalAt = 0;
  private readonly relanceModalDedupMs = 800;
  /** One visible relance modal app-wide (many footers exist in the Ionic stack). */
  private relanceModalPresentationClaimed = false;
  private relanceModalPresentationDemandId: number | null = null;
  private pusherSubscriptions: Subscription[] = [];
  private readonly debugEnabled = true;

  private log(step: string, detail?: Record<string, unknown>): void {
    if (!this.debugEnabled) {
      return;
    }
    if (detail) {
      console.log(`[RelanceFlow] ${step}`, detail);
    } else {
      console.log(`[RelanceFlow] ${step}`);
    }
  }

  constructor(
    private router: Router,
    private navController: NavController,
    private localStorageService: LocalStorageService,
    private pusherService: PusherService
  ) {
    this.registerGlobalRelanceListeners();
  }

  ngOnDestroy(): void {
    this.pusherSubscriptions.forEach((sub) => sub.unsubscribe());
    this.pusherSubscriptions = [];
  }

  /** Single Pusher entry point — avoids N footers each opening their own ion-modal. */
  private registerGlobalRelanceListeners(): void {
    this.pusherSubscriptions.push(
      this.pusherService.DispatcherSignlerProblemObservable().subscribe((res: unknown) => {
        const demandId = this.extractProblemDemandId(res);
        if (demandId != null) {
          this.log("pusher:signaler-problem", { demandId });
          this.openRelanceModal(demandId);
        }
      })
    );

    this.pusherSubscriptions.push(
      this.pusherService.DispatcherDemandeCancelledObservable().subscribe((res: any) => {
        if (this.shouldSuppressCancelModal()) {
          this.log("pusher:cancel-suppressed");
          return;
        }
        const demandId = Number(res?.demande_id);
        if (!Number.isFinite(demandId) || demandId <= 0) {
          return;
        }
        this.log("pusher:demande-cancelled", { demandId });
        this.openRelanceModal(demandId);
      })
    );
  }

  /**
   * Only the active footer may present; blocks stacked page footers from opening duplicates.
   */
  tryClaimRelanceModalPresentation(demandId: number): boolean {
    const id = Number(demandId);
    if (!Number.isFinite(id) || id <= 0) {
      return false;
    }
    if (
      this.relanceModalPresentationClaimed &&
      this.relanceModalPresentationDemandId === id
    ) {
      return false;
    }
    this.relanceModalPresentationClaimed = true;
    this.relanceModalPresentationDemandId = id;
    return true;
  }

  releaseRelanceModalPresentation(): void {
    this.relanceModalPresentationClaimed = false;
    this.relanceModalPresentationDemandId = null;
  }

  registerReloadHandler(handler: ParticulierRelanceReloadHandler | null): void {
    this.reloadHandler = handler;
  }

  hasReloadHandler(): boolean {
    return this.reloadHandler != null;
  }

  /** Ask footer to show the relance modal for this demand (deduped in footer). */
  openRelanceModal(demandId: number | string): void {
    if (this.shouldSuppressCancelModal()) {
      return;
    }
    const id = Number(demandId);
    if (!Number.isFinite(id) || id <= 0) {
      return;
    }
    const now = Date.now();
    if (
      this.lastRelanceModalDemandId === id &&
      now - this.lastRelanceModalAt < this.relanceModalDedupMs
    ) {
      this.log("openRelanceModal:deduped", { demandId: id });
      return;
    }
    this.lastRelanceModalDemandId = id;
    this.lastRelanceModalAt = now;
    this.log("openRelanceModal:emit", { demandId: id });
    this.openModalRequest$.next(id);
  }

  beginRelance(): void {
    this.relancing = true;
    this.suppressCancelUntil = Date.now() + 15000;
    this.log("beginRelance", {
      relanceNavigationDemandId: this.relanceNavigationDemandId,
    });
  }

  endRelance(): void {
    this.log("endRelance", {
      relanceNavigationDemandId: this.relanceNavigationDemandId,
    });
    this.relancing = false;
    this.relanceNavigationDemandId = null;
    this.releaseRelanceModalPresentation();
  }

  isRelanceInProgress(): boolean {
    return this.relancing || this.relanceNavigationDemandId != null;
  }

  shouldSuppressCancelModal(): boolean {
    return this.isRelanceInProgress() || Date.now() < this.suppressCancelUntil;
  }

  isOnServicesAroundRoute(): boolean {
    const path = this.router.url.split("?")[0].split("#")[0];
    return /^\/particulier\/services-around\/\d+$/.test(path);
  }

  /** Current path without query/hash — required for demand-id route guards. */
  getCurrentRoutePath(): string {
    return this.router.url.split("?")[0].split("#")[0];
  }

  extractProblemDemandId(event: unknown): number | null {
    const res = event as {
      demande_id?: unknown;
      data?: {
        problem?: { demande?: { id?: unknown } };
        demande_id?: unknown;
      };
    };
    const candidates = [
      res?.data?.problem?.demande?.id,
      res?.demande_id,
      res?.data?.demande_id,
    ];
    for (const raw of candidates) {
      const id = Number(raw);
      if (Number.isFinite(id) && id > 0) {
        return id;
      }
    }
    return null;
  }

  matchesDemandPage(demandId: number | string): boolean {
    const path = this.getCurrentRoutePath();
    const id = Number(demandId);
    const patterns = [
      /^\/particulier\/loading\/(\d+)$/,
      /^\/particulier\/devis\/(\d+)$/,
      /^\/particulier\/provider-working\/(\d+)$/,
      /^\/particulier\/services-around\/(\d+)$/,
    ];
    for (const re of patterns) {
      const match = path.match(re);
      if (match) {
        const currentId = Number(match[1]);
        return currentId === id || String(currentId) === String(demandId);
      }
    }
    return false;
  }

  async completeDuplicateSuccess(
    res: HttpResponse<unknown>,
    sourceDemandId: number | string
  ): Promise<void> {
    this.suppressCancelUntil = Date.now() + 5000;
    this.localStorageService.dropClientDemand();
    this.localStorageService.removeToCreateDemand();

    const newDemandId = extractDuplicatedDemandId(res);
    if (newDemandId == null) {
      this.log("completeDuplicateSuccess:no-id", { sourceDemandId });
      this.endRelance();
      await this.navController.navigateRoot(["/particulier"]);
      return;
    }

    this.relanceNavigationDemandId = newDemandId;
    this.log("completeDuplicateSuccess", {
      sourceDemandId,
      newDemandId,
      hasReloadHandler: !!this.reloadHandler,
    });
    if (this.reloadHandler) {
      await this.reloadHandler.reloadForNewDemand(
        newDemandId,
        Number(sourceDemandId)
      );
      if (!this.matchesDemandPage(newDemandId)) {
        this.log("completeDuplicateSuccess:handler-fallback-navigate", {
          newDemandId,
        });
        await this.navController.navigateRoot(
          [`/particulier/services-around/${newDemandId}`],
          { animated: false }
        );
      }
      if (this.isRelanceInProgress()) {
        this.endRelance();
      }
      return;
    }

    this.log("completeDuplicateSuccess:fallback-navigate", { newDemandId });
    try {
      await this.navController.navigateRoot(
        [`/particulier/services-around/${newDemandId}`],
        { animated: false }
      );
    } finally {
      this.endRelance();
    }
  }
}
